/**
 * Represents a deferred operation that can be resolved or rejected at a later time.
 * @template T - The type of the value that will be resolved by the promise.
 */
export interface Deferred<T = any> {
    promise: Promise<T>;
    resolve: (value: T) => void;
    reject: (reason?: any) => void;
    resolved: boolean;
    rejected: boolean;
    completed: boolean;
}

/**
 * Creates a deferred operation that can be resolved or rejected at a later time.
 * @template T - The type of the value that will be resolved by the promise.
 * @returns A deferred operation.
 */
export function create<T = any>() {
    /**
     * reference: https://github.com/moll/js-promise-defer/blob/master/index.js
     */
    const deferred: Deferred<T> = Object.create({});
    deferred.promise = new Promise<T>(function(resolve, reject) {
        deferred.resolve = (...rest) => {
            deferred.resolved = true;
            deferred.completed = true;
            resolve(...rest);
        };
        deferred.reject = (...rest) => {
            deferred.rejected = true;
            deferred.completed = true;
            reject(...rest);
        };
    });
    return deferred;
}
